import {ColDef, ICellRendererParams} from "ag-grid-community";
import {t} from "i18next";
import {ReactNode} from "react";
import {dateFormatter} from "../../utils";
import {DeskDiscountGovernanceEntityResponse} from "../../api/types/deskDiscountGovernance";
import {formatDeskDiscountPercentage} from "./deskDiscountFormat";

/*
 * I `field` delle colonne devono combaciare con le chiavi dei filtri (handleTriggerFilters →
 * setFilterModel per colId), altrimenti ag-grid scarta le voci e i filtri non raggiungono il BE.
 * Il rendering usa i valori reali della response via cellRenderer/valueFormatter; l'ordinamento
 * server-side traduce questi colId nei nomi campo BE in useAgGridCustomHook (customSort).
 */
export const deskDiscountGovernanceColDef = ({
                                                 actionsRenderer,
                                                 actionColDef,
                                                 user
                                             }: {
    actionsRenderer: (params: ICellRendererParams) => ReactNode;
    actionColDef: ColDef;
    user: any;
}): ColDef<DeskDiscountGovernanceEntityResponse>[] => [
    {
        headerName: t("pricing:deskDiscountGovernance.id"),
        headerTooltip: t("pricing:deskDiscountGovernance.id"),
        field: "id",
        flex: 0.5,
        sortable: true,
        filter: "text",
    },
    {
        field: "legalEntities",
        headerName: t("pricing:peaks.table.headers.legalEntities"),
        headerTooltip: t("pricing:peaks.table.headers.legalEntities"),
        editable: false,
        flex: 1,
        filter: "text",
        cellRenderer: (params: ICellRendererParams) => t('translation:legalEntities.'.concat(params.data.legalEntity)),
        hide: user.legalEntities.length === 1 && user.dealers.length === 1
    },
    {
        field: "dealers",
        headerName: t("pricing:peaks.table.headers.dealers"),
        headerTooltip: t("pricing:peaks.table.headers.dealers"),
        editable: false,
        flex: 1,
        filter: "text",
        cellRenderer: (params: ICellRendererParams) => t('translation:dealers.'.concat(params.data.dealer)),
        hide: user.legalEntities.length === 1 && user.dealers.length === 1
    },
    {
        headerName: "Location",
        headerTooltip: "Location",
        field: "locationCodes",
        cellRenderer: (params: ICellRendererParams) => params.data.prcLocation?.name || "",
        sortable: true,
        flex: 1.5,
        filter: "text",
    },
    {
        headerName: t("pricing:deskDiscountGovernance.profiles"),
        headerTooltip: t("pricing:deskDiscountGovernance.profiles"),
        field: "lppProfileIds",
        cellRenderer: (params: ICellRendererParams) => params.data.lppProfile?.name || "",
        flex: 1,
        sortable: true,
        filter: "text",
    },
    /*
     * F25 (LPP-21950) - T2.60b, punto 6: le due percentuali passano dal formattatore localizzato.
     * Prima erano rese con `(v / 100).toFixed(2) + "%"`, che scrive sempre il punto decimale: in
     * italiano la griglia mostrava `2.50%` mentre il resto della console usa la virgola.
     */
    {
        headerName: t("pricing:deskDiscountGovernance.minPercentage"),
        headerTooltip: t("pricing:deskDiscountGovernance.minPercentage"),
        field: "discountMin",
        cellRenderer: (params: ICellRendererParams) => formatDeskDiscountPercentage(params.data.discountMin),
        flex: 0.7,
        sortable: true,
        filter: "text",
    },
    {
        headerName: t("pricing:deskDiscountGovernance.maxPercentage"),
        headerTooltip: t("pricing:deskDiscountGovernance.maxPercentage"),
        field: "discountMax",
        cellRenderer: (params: ICellRendererParams) => formatDeskDiscountPercentage(params.data.discountMax),
        flex: 0.7,
        sortable: true,
        filter: "text",
    },
    {
        headerName: t("pricing:deskDiscountGovernance.enabled"),
        headerTooltip: t("pricing:deskDiscountGovernance.enabled"),
        field: "active",
        cellRenderer: (params: ICellRendererParams) =>
            params.data.active ? t("pricing:deskDiscountGovernance.enabledYes") : t("pricing:deskDiscountGovernance.enabledNo"),
        flex: 0.6,
        sortable: true,
        filter: "text",
    },
    /*
     * F25 (LPP-21950) - T2.60b, punto 4: le due colonne mostrano `validityDateFrom`/`validityDateTo`,
     * cioe' il periodo di validita' della configurazione, ma erano intestate con
     * `ancillary.form.labels.reservationFrom/To` ("Prenotazione dal/al"): l'operatore leggeva
     * "Prenotazione" su una data che non ha nulla a che vedere con la prenotazione. Quella chiave e'
     * pre-esistente alla CR e condivisa da ~20 schermate dove le date sono davvero di prenotazione,
     * quindi non si e' cambiato il suo valore: la governance ha due chiavi proprie.
     */
    {
        headerName: t("pricing:deskDiscountGovernance.validityFrom"),
        headerTooltip: t("pricing:deskDiscountGovernance.validityFrom"),
        field: "validityFrom",
        valueFormatter: (params) =>
            params.data?.validityDateFrom ? dateFormatter(params.data?.validityDateFrom) : "",
        flex: 1,
        sortable: true,
        filter: "text",
    },
    {
        headerName: t("pricing:deskDiscountGovernance.validityTo"),
        headerTooltip: t("pricing:deskDiscountGovernance.validityTo"),
        field: "validityTo",
        valueFormatter: (params) =>
            params.data?.validityDateTo ? dateFormatter(params.data?.validityDateTo) : "",
        flex: 1,
        sortable: true,
        filter: "text",
    },
    {
        ...actionColDef,
        cellRenderer: actionsRenderer,
    },
];
